import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useEffect,
} from 'react'
import { useRouter } from 'next/router'
import { useSearchQuery } from '@src/pages/[country]/[language]/search/[searchTerm]'
import {
  DocumentSearchTerms,
  SearchFocusType,
  structureFilterArrayToObject,
} from '@utils/searchUtils'
import {
  NewProductSearchQuery,
  useNewProductSearchQuery,
} from '@src/queries/NewProductSearchQuery.generated'
import {
  ContentDocumentSearchQuery,
  useContentDocumentSearchQuery,
} from '@src/queries/ContentDocumentsSearchQuery.generated'
import {
  ContentSearchType,
  Facet,
  StructureSearchFormat,
  StructureSearchType,
} from '@src/types/graphql-types'
import { useStructureSearchQuery } from '@src/queries/StructureSearchQuery.generated'
import { AppCookies } from '@src/utils/cookies'
import { useCookies } from 'react-cookie'
import { useCurrentUser } from '@src/utils/useCurrentUser'

interface SearchContextType {
  productResults?: NewProductSearchQuery['getNewProductSearchResults']
  contentResults?: ContentDocumentSearchQuery['getContentDocuments']
  loading: boolean
  metadata: any
  facets: Facet[] | undefined
  hasResults: boolean
  isStructureSearch: boolean
  isDocumentSearch: boolean
  searchTerm: string
  facetTerm: string
}

const SearchContext = createContext<SearchContextType | undefined>(undefined)

export const useSearchContext = () => {
  const context = useContext(SearchContext)

  if (!context)
    throw new Error('useSearchContext must be used within a SearchProvider')

  return context
}

export const SearchProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [query, parsedFacets] = useSearchQuery()
  const user = useCurrentUser()
  const router = useRouter()
  const [, setCookie] = useCookies([AppCookies.SrpExperience])
  const focus = query.focus
  const searchTerm = query.term || ''
  const facetTerm = query.facet[0]?.split(':')[1]

  const isCasNumberSearch = useMemo(
    () => query?.type === 'cas_number',
    [query?.type]
  )

  useEffect(() => {
    if (isCasNumberSearch) {
      setCookie(AppCookies.SrpExperience, 'old', { path: '/' })
      router.replace(router.asPath)
    }
  }, [isCasNumberSearch])

  const isStructureSearch = focus === SearchFocusType.StructureSearch
  const isDocumentSearch = DocumentSearchTerms.has(searchTerm.toLowerCase())

  const productSearch = useNewProductSearchQuery({
    skip: focus !== SearchFocusType.Products && focus !== SearchFocusType.B2B,
    variables: {
      term: searchTerm,
      facets: parsedFacets,
      count: 10,
      page: query.page,
      isBbeCatalogEnabled: user.isBbeCatalogEnabled,
    },
  })

  const contentSearchType =
    focus === SearchFocusType.SiteContent
      ? ContentSearchType.SiteContent
      : ContentSearchType.Documents

  const contentSearch = useContentDocumentSearchQuery({
    skip: focus === SearchFocusType.Products || focus === SearchFocusType.B2B,
    variables: {
      searchTerm,
      page: query.page,
      perPage: 10,
      selectedFacets: parsedFacets,
      contentSearchType,
    },
  })

  const structureSearch = useStructureSearchQuery({
    skip: !isStructureSearch,
    variables: {
      type: query.type as StructureSearchType,
      term: searchTerm || '',
      format: query.format as StructureSearchFormat,
      sort: query.sort,
      pagination: {
        page: query.page,
        perPage: query.perpage,
      },
      filters: structureFilterArrayToObject(query.filters),
      selectedFacets: parsedFacets,
      catalogType: query.catalogType,
    },
  })

  const results = useMemo(() => {
    if (focus === SearchFocusType.Products || focus === SearchFocusType.B2B) {
      return {
        productResults: productSearch.data?.getNewProductSearchResults,
        contentResults: undefined,
        loading: productSearch.loading,
        metadata: productSearch.data?.getNewProductSearchResults?.metadata,
        facets: productSearch.data?.getNewProductSearchResults
          ?.facets as Facet[],
        hasResults:
          !!productSearch.data?.getNewProductSearchResults?.metadata?.count,
      }
    }

    if (isStructureSearch) {
      const data = structureSearch.data?.getStructureSearchResults
      return {
        structureResults: data,
        loading: structureSearch.loading,
        metadata: data?.metadata,
        facets: data?.facets,
        hasResults: (data?.items.length ?? 0) > 0,
      }
    }

    return {
      productResults: undefined,
      contentResults: contentSearch.data?.getContentDocuments,
      loading: contentSearch.loading,
      metadata: contentSearch.data?.getContentDocuments?.metadata,
      facets: contentSearch.data?.getContentDocuments?.facets,
      hasResults:
        !!contentSearch.data?.getContentDocuments?.metadata?.itemCount,
    }
  }, [focus, productSearch, contentSearch])

  const value = useMemo<SearchContextType>(
    () => ({
      ...results,
      isStructureSearch,
      isDocumentSearch,
      searchTerm,
      facetTerm,
    }),
    [results, isStructureSearch, searchTerm, facetTerm]
  )

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  )
}

export default SearchProvider
